/**
 * AssemblyResultsPage - Full page view for assembly tariff analysis results
 *
 * Displays complete assembly tariff analysis results including:
 * - Interactive Decision Tree Wizard for HTS classification
 * - Assembly Landed Cost Calculator (appears after classification)
 * - Material Weight Summary with Section 232 focus
 * - HTS Code Distribution chart
 * - Component breakdown details
 *
 * This follows the TARIFF_ASSEMBLY_MIGRATION_GUIDE.md specification
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { IoCubeOutline } from 'react-icons/io5';
import { FiPackage, FiClock, FiCheckCircle, FiAlertCircle, FiFileText, FiBarChart2, FiGrid, FiList, FiEdit3, FiInfo } from 'react-icons/fi';
import { IoGitBranch } from 'react-icons/io5';
import { HiArrowDownTray } from 'react-icons/hi2';
import axios from 'axios';
import DecisionTreeWizard from './DecisionTreeWizard';
import AssemblyTariffCalculator from './AssemblyTariffCalculator';
import MaterialPieChart from './MaterialPieChart';
import HTSDistributionChart from './HTSDistributionChart';
import ComponentCard from './ComponentCard';
import FileDetailPanel from './FileDetail';
import MaterialWeightSummary from './MaterialWeightSummary';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

// BOM Tree Node component for recursive tree display
function BOMTreeNode({ node, depth = 0, components, onComponentClick }) {
    const [isExpanded, setIsExpanded] = useState(depth < 2);
    const hasChildren = node.children && node.children.length > 0;
    const isMatched = node.status === 'matched';

    // Find the component data for this node
    const componentData = components?.find(c =>
        c.part_number === node.part_number || c.file_id === node.file_id
    );
    const weight = componentData?.weight_kg || node.weight_kg || 0;

    return (
        <div className="select-none">
            <div
                className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-colors
                            ${depth === 0 ? 'bg-[#1e5045]/10' : 'hover:bg-gray-50'}
                            ${hasChildren ? 'cursor-pointer' : ''}`}
                style={{ marginLeft: `${depth * 24}px` }}
                onClick={() => hasChildren && setIsExpanded(!isExpanded)}
            >
                {/* Expand/collapse toggle */}
                {hasChildren ? (
                    <button className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600">
                        {isExpanded ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        )}
                    </button>
                ) : (
                    <div className="w-5 h-5" />
                )}

                {/* Status indicator */}
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${
                    isMatched
                        ? 'text-green-600 bg-green-50 border-green-200'
                        : 'text-gray-600 bg-gray-50 border-gray-200'
                }`}>
                    {isMatched ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                    )}
                </span>

                {/* Part info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span
                            className={`font-mono font-medium truncate ${
                                isMatched ? 'text-gray-900 cursor-pointer hover:text-[#1e5045]' : 'text-gray-500'
                            }`}
                            onClick={(e) => {
                                if (isMatched && componentData?.file_id && onComponentClick) {
                                    e.stopPropagation();
                                    onComponentClick(componentData.file_id);
                                }
                            }}
                        >
                            {node.part_number || 'Unknown'}
                        </span>
                        {node.qty > 1 && (
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                x{node.qty}
                            </span>
                        )}
                    </div>
                    {node.description && (
                        <div className="text-sm text-gray-500 truncate">
                            {node.description}
                        </div>
                    )}
                </div>

                {/* Weight - show asterisk for parent assemblies to indicate weight not counted in totals */}
                {weight > 0 && (
                    <span
                        className={`text-sm font-medium ${hasChildren ? 'text-gray-400' : 'text-gray-600'}`}
                        title={hasChildren ? 'Parent assembly weight - not included in totals to avoid double-counting with children' : ''}
                    >
                        {weight.toFixed(2)} kg
                        {hasChildren && (
                            <span className="text-amber-600 ml-0.5">*</span>
                        )}
                    </span>
                )}

                {/* Status badge */}
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    isMatched
                        ? 'text-green-600 bg-green-50 border border-green-200'
                        : 'text-gray-600 bg-gray-50 border border-gray-200'
                }`}>
                    {isMatched ? 'Matched' : 'Missing'}
                </span>
            </div>

            {/* Children (recursive) */}
            {hasChildren && isExpanded && (
                <div className="border-l-2 border-gray-200 ml-6">
                    {node.children.map((child, idx) => (
                        <BOMTreeNode
                            key={`${child.part_number}-${idx}`}
                            node={child}
                            depth={depth + 1}
                            components={components}
                            onComponentClick={onComponentClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Normalize raw material names to parent categories for grouping.
 * Maps specific material names (e.g., "Ultraform H4320") to parent categories (e.g., "Plastics").
 */
const normalizeMaterialToParent = (material) => {
    if (!material) return 'Other/Unknown';

    const m = material.toLowerCase().trim();

    // Steel variants
    if (m.includes('steel') || m.includes('stainless') || m.includes('iron') ||
        m.includes('ferrous') || m.includes('s355') || m.includes('s235') ||
        m.includes('dc01') || m.includes('dd11') || m.includes('dx51') ||
        m.includes('galvanized') || m.includes('hot rolled') || m.includes('cold rolled')) {
        return 'Steel';
    }

    // Aluminum variants
    if (m.includes('aluminum') || m.includes('aluminium') || m.includes('al ') ||
        m.includes('al-') || m.match(/^al\d/) || m.includes('almg') ||
        m.includes('6061') || m.includes('6063') || m.includes('5052') ||
        m.includes('5083') || m.includes('7075') || m.includes('2024')) {
        return 'Aluminum';
    }

    // Copper/Brass variants
    if (m.includes('copper') || m.includes('brass') || m.includes('bronze') ||
        m.includes('cuzn') || m.includes('cu-') || m.includes('beryllium')) {
        return 'Copper/Brass';
    }

    // Plastics/Polymers - comprehensive list
    if (m.includes('plastic') || m.includes('polymer') || m.includes('nylon') ||
        m.includes('polyamide') || m.includes('pa6') || m.includes('pa66') ||
        m.includes('polypropylene') || m.includes('polyethylene') || m.includes('pvc') ||
        m.includes('polycarbonate') || m.includes('abs') || m.includes('acetal') ||
        m.includes('pom') || m.includes('ultraform') || m.includes('delrin') ||
        m.includes('teflon') || m.includes('ptfe') || m.includes('peek') ||
        m.includes('pps') || m.includes('pet') || m.includes('hdpe') || m.includes('ldpe') ||
        m.includes('zytel') || m.includes('akulon') || m.includes('foam') ||
        m.includes('urethane') || m.includes('poron') || m.includes('plastazote') ||
        m.includes('armaflex') || m.includes('tpe') || m.includes('silicone') ||
        m.includes('rubber') || m.includes('epdm') || m.includes('nbr') ||
        m.includes('elastomer') || m.includes('exolon') || m.includes('polyvantis') ||
        m.includes('lastrocel') || m.includes('cellular') || m.includes('expanded')) {
        return 'Plastics/Polymers';
    }

    // Paper/Cardboard
    if (m.includes('paper') || m.includes('cardboard') || m.includes('carton') ||
        m.includes('corrugated') || m.includes('fasson') || m.includes('label') ||
        m.includes('viledon') || m.includes('filter')) {
        return 'Paper/Fiber';
    }

    // Glass/Ceramics
    if (m.includes('glass') || m.includes('ceramic') || m.includes('quartz')) {
        return 'Glass/Ceramics';
    }

    // Other metals
    if (m.includes('titanium') || m.includes('nickel') || m.includes('zinc') ||
        m.includes('tin') || m.includes('magnesium') || m.includes('lead')) {
        return 'Other Metals';
    }

    // Multi-material / Assembly
    if (m.includes('assembly') || m.includes('multiple material') || m.includes('see bom') ||
        m.includes('various')) {
        return 'Multi-Material';
    }

    // Not specified / Unknown
    if (m.includes('not specified') || m.includes('unspecified') || m.includes('unknown') ||
        m.includes('n/a') || m === 'other' || m === 'na' || m === 'none') {
        return 'Other/Unknown';
    }

    // Default fallback - group unrecognized materials as Other/Unknown
    return 'Other/Unknown';
};

/**
 * Compute material summary from components and assembly tree
 * Uses leaf-node detection to avoid double-counting assembly weights
 */
const computeMaterialSummaryFromTree = (components, assemblyTree) => {
    const materialMap = {};
    let totalWeight = 0;
    let pdfsWithWeight = 0;

    // Build maps from tree: file_id -> hasChildren, file_id -> qty
    const nodesWithChildren = new Set();
    const qtyMap = {};  // file_id -> quantity from BOM

    const traverseTree = (node) => {
        if (!node) return;
        const matchedChildren = (node.children || []).filter(c => c.status === 'matched');
        const fileId = node.matched_doc?.file_id;

        if (fileId) {
            // Track if this node has matched children (is a parent assembly)
            if (matchedChildren.length > 0) {
                nodesWithChildren.add(fileId);
            }
            // Track quantity from BOM (default to 1)
            qtyMap[fileId] = node.qty || 1;
        }

        (node.children || []).forEach(traverseTree);
    };

    if (assemblyTree?.root) {
        traverseTree(assemblyTree.root);
    }

    // Also process orphan components (they're always leaves with qty=1)
    const orphanComponents = assemblyTree?.orphan_components || [];
    orphanComponents.forEach(orphan => {
        const fileId = orphan.matched_doc?.file_id;
        if (fileId) {
            qtyMap[fileId] = orphan.qty || 1;
        }
    });

    // Process each component
    components.forEach(comp => {
        const unitWeight = parseFloat(comp.weight_kg) || 0;
        const qty = qtyMap[comp.file_id] || 1;  // Get qty from tree, default 1
        const extendedWeight = unitWeight * qty;  // Weight × Quantity
        const isLeaf = !nodesWithChildren.has(comp.file_id);
        const shouldCountWeight = isLeaf && unitWeight > 0;

        // Determine material category
        const material = comp.material || '';
        const sec232Material = (comp.sec232_material || '').toLowerCase();
        const llmMaterialParent = (comp.material_parent || '').trim();

        // Valid LLM parent categories (must match exactly)
        const validParentCategories = [
            'Steel', 'Aluminum', 'Plastics/Polymers', 'Copper/Brass',
            'Rubber/Elastomers', 'Paper/Fiber', 'Glass/Ceramics',
            'Other Metals', 'Other/Unknown'
        ];

        // Priority: sec232_material > LLM material_parent > frontend normalization
        let materialName;
        if (sec232Material === 'steel') {
            materialName = 'Steel';
        } else if (sec232Material === 'aluminum' || sec232Material === 'aluminium') {
            materialName = 'Aluminum';
        } else if (llmMaterialParent && validParentCategories.includes(llmMaterialParent)) {
            materialName = llmMaterialParent;
        } else {
            materialName = normalizeMaterialToParent(material);
        }

        if (!materialMap[materialName]) {
            materialMap[materialName] = { weight: 0, count: 0, components: [] };
        }

        // Only count weight from leaf nodes (using extended weight)
        if (shouldCountWeight) {
            pdfsWithWeight++;
            totalWeight += extendedWeight;
            materialMap[materialName].weight += extendedWeight;
        }

        materialMap[materialName].count++;
        materialMap[materialName].components.push({
            file_id: comp.file_id,
            part_number: comp.part_number || comp.filename,
            filename: comp.filename,
            material: comp.material,
            weight_kg: extendedWeight,  // Show extended weight (qty × unit)
            unit_weight_kg: unitWeight,  // Keep unit weight for reference
            qty: qty,
            weight_counted: shouldCountWeight,  // Flag: is this weight included in totals?
            hts_code: comp.hts_code,
            hts_description: comp.hts_description,
            is_leaf: isLeaf
        });
    });

    // Convert to ordered array with percentages
    const orderedMaterials = Object.entries(materialMap)
        .map(([name, data]) => ({
            name,
            weight: data.weight,
            count: data.count,
            components: data.components.sort((a, b) => (b.weight_kg || 0) - (a.weight_kg || 0)),
            percentage: totalWeight > 0 ? (data.weight / totalWeight) * 100 : 0
        }))
        .sort((a, b) => {
            // Priority: Steel first, Aluminum second, then by weight
            if (a.name === 'Steel') return -1;
            if (b.name === 'Steel') return 1;
            if (a.name === 'Aluminum') return -1;
            if (b.name === 'Aluminum') return 1;
            return b.weight - a.weight;
        });

    return {
        summary: {
            pdfs_with_weight: pdfsWithWeight,
            total_analyzed: components.length,
            total_weight: totalWeight
        },
        totalWeight,
        orderedMaterials
    };
};

// BOM Tree View component
function BOMTreeView({ assemblyTree, components, onComponentClick }) {
    const hasRoot = assemblyTree?.root;
    const orphanComponents = assemblyTree?.orphan_components || [];
    const hasOrphans = orphanComponents.length > 0;

    if (!hasRoot && !hasOrphans) {
        return (
            <div className="text-center py-8 text-gray-500">
                <IoGitBranch className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No BOM tree structure available</p>
                <p className="text-sm mt-2">Upload documents with BOM tables to see the assembly hierarchy</p>
            </div>
        );
    }

    const { root, matched_count = 0, missing_count = 0 } = assemblyTree;

    return (
        <div>
            {/* Summary stats */}
            <div className="flex gap-6 mb-4 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{matched_count}</span> Matched
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                    <span className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{missing_count}</span> Missing
                    </span>
                </div>
                {hasOrphans && (
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-400"></span>
                        <span className="text-sm text-gray-600">
                            <span className="font-semibold text-gray-900">{orphanComponents.length}</span> Standalone
                        </span>
                    </div>
                )}
            </div>

            {/* Tree view */}
            <div className="max-h-[500px] overflow-y-auto">
                {/* Main assembly tree */}
                {root && (
                    <BOMTreeNode
                        node={root}
                        depth={0}
                        components={components}
                        onComponentClick={onComponentClick}
                    />
                )}

                {/* Orphan components (not in any BOM) */}
                {hasOrphans && (
                    <div className={root ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                        {root && (
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 px-3">
                                Standalone Components (not in BOM)
                            </div>
                        )}
                        {orphanComponents.map((orphan, idx) => (
                            <BOMTreeNode
                                key={`orphan-${orphan.part_number}-${idx}`}
                                node={orphan}
                                depth={0}
                                components={components}
                                onComponentClick={onComponentClick}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Weight counting footnote */}
            <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500">
                <span className="text-amber-600 font-medium">*</span> Parent assembly weights are shown for reference but not included in material totals to avoid double-counting with their child components.
            </div>
        </div>
    );
}

export default function AssemblyResultsPage() {
    const { assemblyId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [classificationResult, setClassificationResult] = useState(null);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [committedCalculation, setCommittedCalculation] = useState(null);
    const [summaryViewMode, setSummaryViewMode] = useState('material'); // 'material' or 'bom'
    const token = sessionStorage.getItem("userToken");

    // Get passed state from navigation
    const passedState = location.state || {};
    const folderName = passedState.folderName;
    const spacePath = passedState.spacePath;

    useEffect(() => {
        if (!assemblyId) return;

        const fetchAnalysis = async () => {
            try {
                setLoading(true);
                const res = await axios.get(
                    `${BASE_URL}/ocr/assembly-analysis/${assemblyId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAnalysis(res.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch assembly analysis:', err);
                setError(err.response?.data?.error || 'Failed to load assembly analysis');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalysis();
    }, [assemblyId, token]);

    // Fetch committed calculation if it exists
    useEffect(() => {
        if (!assemblyId) return;

        const fetchCommittedCalculation = async () => {
            try {
                const res = await axios.get(
                    `${BASE_URL}/ocr/committed-calculation/${assemblyId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.data.success && res.data.committed) {
                    setCommittedCalculation(res.data.committed);
                }
            } catch (err) {
                // 404 is expected if no committed calculation exists
                if (err.response?.status !== 404) {
                    console.error('Failed to fetch committed calculation:', err);
                }
            }
        };

        fetchCommittedCalculation();
    }, [assemblyId, token]);

    // Load saved classification from analysis or committed calculation if it exists
    useEffect(() => {
        // Priority 1: ALWAYS prefer analysis.selected_classification (has full rationale from wizard)
        // This should override any previous value from committed calculation
        if (analysis?.selected_classification) {
            // Only update if different to avoid infinite loops
            if (classificationResult?.htsCode !== analysis.selected_classification.htsCode ||
                classificationResult?.rationale !== analysis.selected_classification.rationale) {
                console.log('Loading classification from analysis:', analysis.selected_classification);
                setClassificationResult(analysis.selected_classification);
            }
            return;
        }

        // Priority 2: Use committed calculation values if available (fallback when no explicit classification saved)
        // Only use this if we don't already have a classification result
        if (!classificationResult && committedCalculation?.formData) {
            const fd = committedCalculation.formData;
            console.log('Restoring classification from committed calculation:', fd, committedCalculation);
            setClassificationResult({
                htsCode: fd.htsCode || '',
                dutyRate: fd.generalDutyPercent || 0,
                // Use saved rationale if available, otherwise show placeholder
                rationale: committedCalculation.rationale || 'Restored from committed calculation',
                notes: '',
                sec301Applies: fd.sec301Applies ?? false,
                sec301List: fd.sec301List || '',
                sec232Applies: false,
            });
        }
    }, [analysis, classificationResult, committedCalculation]);

    // Handle classification completion from wizard - also persist to backend
    const handleClassificationComplete = useCallback(async (result) => {
        setClassificationResult(result);

        // Persist the selection to the database
        if (assemblyId && result.htsCode) {
            try {
                await axios.post(
                    `${BASE_URL}/ocr/assembly-analysis/${assemblyId}/select-hts/`,
                    {
                        htsCode: result.htsCode,
                        dutyRate: result.dutyRate,
                        rationale: result.rationale,
                        notes: result.notes,
                        sec301Applies: result.sec301Applies,
                        sec301List: result.sec301List,
                        sec232Applies: result.sec232Applies,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('HTS selection persisted to database');
            } catch (err) {
                console.error('Failed to persist HTS selection:', err);
                // Don't block the user - selection still works locally
            }
        }
    }, [assemblyId, token]);

    const formatTimestamp = (ts) => {
        if (!ts) return 'Unknown date';
        try {
            const year = ts.substring(0, 4);
            const month = ts.substring(4, 6);
            const day = ts.substring(6, 8);
            const hour = ts.substring(9, 11);
            const minute = ts.substring(11, 13);
            const second = ts.substring(13, 15) || '00';

            const utcDate = new Date(Date.UTC(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hour),
                parseInt(minute),
                parseInt(second)
            ));

            return utcDate.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        } catch {
            return ts;
        }
    };

    const handleBack = () => {
        if (folderName) {
            history.goBack();
        } else {
            history.push('/OrgChart');
        }
    };





    // Main export function
    const exportToExcel = async () => {
        const targetFolder = folderName || analysis?.metadata?.folder_name;
        const targetSpace = spacePath || analysis?.metadata?.space_path;
        
        if (!targetFolder) {
            alert('Folder name is missing. Cannot export.');
            return;
        }

        setIsExporting(true);
        try {
            const response = await axios.get(`${BASE_URL}/ocr/export/excel/`, {
                params: {
                    folder_name: targetFolder,
                    space_path: targetSpace,
                    assembly_id: assemblyId  // Pass assembly_id directly to ensure correct committed calculation is fetched
                },
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important
            });

            // Create a link to download the file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from header if possible, or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `SourceOptima_Tariff_Analysis_${new Date().toISOString().slice(0,10)}.xlsx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch.length === 2)
                    filename = filenameMatch[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Excel export failed:', err);
            alert('Failed to export Excel file. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e5045] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading assembly analysis...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <FiAlertCircle className="w-12 h-12 text-[#f26522] mx-auto" />
                    <p className="mt-4 text-[#f26522]">{error}</p>
                    <button
                        onClick={handleBack}
                        className="mt-4 px-4 py-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 border border-[#1e5045]/10"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // Extract data from analysis
    const result = analysis?.result || {};
    const metadata = analysis?.metadata || {};
    const componentBreakdown = result.component_breakdown || {};
    const htsRollup = result.hts_rollup || {};
    const decisionTree = result.decision_tree;
    const materialSummary = result.material_summary || {};
    const materialMapping = result.material_mapping || {};
    const components = result.components || [];
    const assemblyTree = result.assembly_tree || analysis?.assembly_tree || {};

    // Compute material summary using tree structure (leaf-node detection)
    // This avoids double-counting assembly weights by only counting leaf nodes
    const computedMaterialSummary = computeMaterialSummaryFromTree(components, assemblyTree);
    const totalWeight = computedMaterialSummary.totalWeight;
    const orderedMaterials = computedMaterialSummary.orderedMaterials;
    const computedSummary = computedMaterialSummary.summary;

    // Build material weight map from computed summary for weightData
    const computedMaterialWeights = {};
    orderedMaterials.forEach(mat => {
        computedMaterialWeights[mat.name] = mat.weight;
    });

    // Prepare weight data for calculator - using computed leaf-node weights
    const weightData = {
        total_weight_kg: totalWeight,
        steel_weight_kg: computedMaterialWeights.Steel || 0,
        aluminum_weight_kg: computedMaterialWeights.Aluminum || 0,
    };

    // Get material summary for chart - use computed weights
    const materialChartData = computedMaterialWeights;

    // Convert htsRollup to chart format
    const htsChartData = Object.entries(htsRollup).reduce((acc, [code, data]) => {
        acc[code] = data.count || 1;
        return acc;
    }, {});

    // Sorted HTS table data
    const htsTableData = Object.entries(htsRollup)
        .map(([code, data]) => ({
            code,
            count: data.count || 1,
            description: data.description || code,
            totalWeight: data.total_weight || 0
        }))
        .sort((a, b) => b.count - a.count);

    return (
        <div className="min-h-screen bg-[#f6f4ed]">
            {/* Header */}
            <div className="bg-white border-b border-[#1e5045]/10 sticky top-0 z-10">
                <div className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <FaArrowLeft className="w-4 h-4" />
                        <span>Back to Project</span>
                    </button>
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 justify-center">
                            <IoCubeOutline className="w-7 h-7 text-[#1e5045]" />
                            Tariff Analysis (Assembly Mode)
                        </h1>
                        <p className="text-sm text-gray-500">
                            {folderName || metadata.folder_name} • {analysis?.component_count || metadata.component_count || 0} files analyzed
                        </p>
                    </div>
                    <button
                        disabled={isExporting || !components.length}
                        onClick={exportToExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1e3a8a] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1e40af]"
                    >
                        <HiArrowDownTray className="w-4 h-4" />
                        {isExporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6 max-w-7xl mx-auto space-y-6">
                {/* Workflow Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="font-medium text-blue-900 mb-1">About These Results</h3>
                            <p className="text-sm text-blue-700 mb-3">
                                These results are based on <strong>automatic BOM reconstruction</strong> by the AI.
                                The decision tree and material weights are calculated from individual component analysis.
                                For best accuracy, you can review and correct the BOM structure before finalizing.
                            </p>
                            <button
                                onClick={() => history.push(`/bom-approval/${assemblyId}`)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <FiEdit3 className="w-4 h-4" />
                                Review & Approve BOM
                            </button>
                        </div>
                    </div>
                </div>

                {/* Summary Banner */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-start gap-6">
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#1e5045] to-[#225953] flex items-center justify-center flex-shrink-0">
                            <FiPackage className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Assembly Tariff Classification
                            </h2>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                                <div className="flex items-center gap-1.5">
                                    <FiFileText className="w-4 h-4" />
                                    <span>{analysis?.component_count || metadata.component_count || 0} components</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <FiClock className="w-4 h-4" />
                                    <span>{formatTimestamp(analysis?.timestamp)}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <FiCheckCircle className="w-4 h-4 text-[#94c43d]" />
                                    <span className="text-[#94c43d]">Completed</span>
                                </div>
                            </div>
                        </div>
                        {classificationResult && (
                            <div className="text-right">
                                <div className="text-sm text-gray-500">Final HTS</div>
                                <div className="text-xl font-mono font-bold text-[#f26522]">
                                    {classificationResult.htsCode}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Decision Tree Wizard */}
                {decisionTree && (
                    <div>
                        <DecisionTreeWizard
                            treeData={decisionTree}
                            onClassificationComplete={handleClassificationComplete}
                            materialSummary={materialSummary.summary}
                            savedClassification={classificationResult}
                        />

                        {/* Calculator appears after classification is complete */}
                        {classificationResult && (
                            <div className="mt-6">
                                <AssemblyTariffCalculator
                                    assemblyId={assemblyId}
                                    weightData={weightData}
                                    materialSummary={computedMaterialWeights}
                                    defaultHtsCode={classificationResult.htsCode}
                                    defaultDutyRate={classificationResult.dutyRate}
                                    rationale={classificationResult.rationale}
                                    folderName={folderName}
                                    spacePath={spacePath}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* If no decision tree, show calculator directly */}
                {!decisionTree && (
                    <div className="bg-white rounded-xl shadow-sm border border-[#1e5045]/10 p-6">
                        <div className="text-center mb-6">
                            <FiAlertCircle className="w-12 h-12 mx-auto text-[#f26522] mb-3" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                No Decision Tree Available
                            </h3>
                            <p className="text-gray-500">
                                Use the calculator below with manual HTS code entry
                            </p>
                        </div>
                        <AssemblyTariffCalculator
                            assemblyId={assemblyId}
                            weightData={weightData}
                            materialSummary={computedMaterialWeights}
                            defaultHtsCode=""
                            defaultDutyRate={0}
                            folderName={folderName}
                            spacePath={spacePath}
                        />
                    </div>
                )}

                {/* Material Weight Summary / BOM Tree Toggle Section */}
                {(orderedMaterials.length > 0 || assemblyTree?.root) && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        {/* Header with Toggle */}
                        <div className="p-6 pb-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-semibold text-[#1e5045]">
                                    {summaryViewMode === 'material' ? 'Material Weight Summary' : 'BOM Tree Structure'}
                                </h3>
                                <p className="text-sm text-[#225953] mt-1">
                                    {summaryViewMode === 'material'
                                        ? 'Section 232 Focus: Steel & Aluminum Analysis'
                                        : 'Assembly hierarchy from BOM analysis'}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* View Toggle */}
                                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                                    <button
                                        onClick={() => setSummaryViewMode('material')}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                            summaryViewMode === 'material'
                                                ? 'bg-white text-[#1e5045] shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <FiList className="w-4 h-4 inline mr-1.5" />
                                        Materials
                                    </button>
                                    <button
                                        onClick={() => setSummaryViewMode('bom')}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                            summaryViewMode === 'bom'
                                                ? 'bg-white text-[#1e5045] shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        <IoGitBranch className="w-4 h-4 inline mr-1.5" />
                                        BOM Tree
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="px-6 pb-6">
                            {summaryViewMode === 'material' ? (
                                <MaterialWeightSummary
                                    materialSummary={computedSummary}
                                    totalWeight={totalWeight}
                                    orderedMaterials={orderedMaterials}
                                    components={components}
                                    onComponentClick={(fileId) => {
                                        const element = document.getElementById(`file-card-${fileId}`);
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            element.classList.add('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
                                            setTimeout(() => {
                                                element.classList.remove('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
                                            }, 2000);
                                        }
                                    }}
                                    embedded={true}
                                />
                            ) : (
                                /* BOM Tree View */
                                <BOMTreeView
                                    assemblyTree={assemblyTree}
                                    components={components}
                                    onComponentClick={(fileId) => {
                                        const element = document.getElementById(`file-card-${fileId}`);
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            element.classList.add('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
                                            setTimeout(() => {
                                                element.classList.remove('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
                                            }, 2000);
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Material Breakdown Chart */}
                    {Object.keys(materialChartData).length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FiBarChart2 className="w-5 h-5 text-[#1e5045]" />
                                Material Distribution (by weight)
                            </h3>
                            <div className="h-64">
                                <MaterialPieChart data={materialChartData} totalWeight={materialSummary.total_weight || 0} />
                            </div>
                        </div>
                    )}

                    {/* HTS Distribution Chart */}
                    {Object.keys(htsChartData).length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FiBarChart2 className="w-5 h-5 text-[#1e5045]" />
                                HTS Code Distribution
                            </h3>
                            <div className="h-64">
                                <HTSDistributionChart data={htsChartData} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Component File Cards */}
                {components.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 pb-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FiGrid className="w-5 h-5 text-[#1e5045]" />
                                    Component Details
                                </h3>
                                <span className="text-sm text-gray-500">
                                    {components.length} components
                                </span>
                            </div>
                        </div>
                        <div className="px-6 pb-6">
                            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {components.map((component, idx) => (
                                    <ComponentCard
                                        key={component.file_id || idx}
                                        id={`file-card-${component.file_id}`}
                                        component={component}
                                        onClick={setSelectedComponent}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Fallback: HTS Rollup Table (if no components array) */}
                {components.length === 0 && htsTableData.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 pb-0">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Component HTS Code Details
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-y border-gray-200">
                                        <th className="px-6 py-3 text-left font-semibold text-gray-700">HTS Code</th>
                                        <th className="px-6 py-3 text-left font-semibold text-gray-700">Description</th>
                                        <th className="px-6 py-3 text-center font-semibold text-gray-700">Count</th>
                                        <th className="px-6 py-3 text-right font-semibold text-gray-700">Total Weight</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {htsTableData.map((item) => (
                                        <tr key={item.code} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-mono text-gray-900 font-medium">
                                                {item.code}
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">
                                                {item.description || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="bg-[#94c43d]/20 text-[#1e5045] px-2 py-0.5 rounded-full text-xs font-medium">
                                                    {item.count}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right text-gray-900">
                                                {item.totalWeight > 0 ? `${item.totalWeight.toFixed(2)} kg` : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Component Detail Panel (Modal) - Use FileDetailPanel for consistency */}
            {selectedComponent && (
                <FileDetailPanel
                    file={{
                        filename: selectedComponent.filename,
                        _id: selectedComponent.file_id,
                        base_metadata: { s3_url: selectedComponent.s3_url }
                    }}
                    fileId={selectedComponent.file_id}
                    filename={selectedComponent.filename}
                    onClose={() => setSelectedComponent(null)}
                    defaultTab="tariff"
                />
            )}
        </div>
    );
}
